import {
  BellOutlined,
  CalendarOutlined,
  CarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  FileProtectOutlined,
  InboxOutlined,
  NotificationOutlined,
  ReadOutlined,
  UndoOutlined,
  WarningOutlined,
  DiffOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  ALERT_FAMILY_LABELS,
  alertPath,
  describeAlert,
  type AlertFamily,
  type Locale,
} from '@wm/domain';
import {
  App,
  Badge,
  Button,
  Collapse,
  Drawer,
  Dropdown,
  Empty,
  Flex,
  Segmented,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { errorMessage, useApi } from '../api/client';
import { keys, useAlertSummary, useAlerts, useBulletins } from '../api/hooks';
import type { AlertItem, Bulletin } from '../api/types';
import { formatDate } from '../format';
import { brand, brandRamp, status } from '../theme';

const SEVERITY_COLOR: Record<AlertItem['severity'], string> = {
  CRITICAL: status.danger,
  WARNING: status.warning,
  INFO: brand.primary,
};

const FAMILY_ICON: Record<AlertFamily, React.ReactNode> = {
  DOCUMENT_EXPIRY: <FileProtectOutlined />,
  TRACEABILITY: <CarOutlined />,
  STOCK: <InboxOutlined />,
  EVENT: <WarningOutlined />,
  PICKUP_REQUEST: <CalendarOutlined />,
};

const locale = (lang: string): Locale =>
  lang.startsWith('ar') ? 'ar' : lang.startsWith('en') ? 'en' : 'fr';

const OPEN_EVENT = 'wm:open-notifications';

/** Opens the notification centre, on one alert family when given (dashboard links, FR-PIL-03). */
export function openNotifications(family?: AlertFamily) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { family } }));
}

/** The bell of the header: open alerts not yet seen and unread bulletins (FR-TRV-01). */
export function NotificationBell() {
  const { t } = useTranslation();
  const summary = useAlertSummary();
  const [open, setOpen] = useState(false);
  const [family, setFamily] = useState<AlertFamily>();
  useEffect(() => {
    const listener = (e: Event) => {
      setFamily((e as CustomEvent<{ family?: AlertFamily }>).detail?.family);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, listener);
    return () => window.removeEventListener(OPEN_EVENT, listener);
  }, []);
  const unread = (summary.data?.unread ?? 0) + (summary.data?.unreadBulletins ?? 0);
  const critical = (summary.data?.critical ?? 0) > 0;
  return (
    <>
      <Tooltip title={t('notifications.title')}>
        <Badge
          count={unread}
          size="small"
          overflowCount={99}
          color={critical ? undefined : brand.background}
          offset={[-4, 4]}
        >
          <Button
            type="text"
            icon={<BellOutlined />}
            aria-label={t('notifications.title')}
            onClick={() => setOpen(true)}
          />
        </Badge>
      </Tooltip>
      <NotificationsDrawer
        open={open}
        family={family}
        onClose={() => {
          setOpen(false);
          setFamily(undefined);
        }}
      />
    </>
  );
}

function NotificationsDrawer({
  open,
  family,
  onClose,
}: {
  open: boolean;
  family?: AlertFamily;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const summary = useAlertSummary();
  const [tab, setTab] = useState<'open' | 'done' | 'news'>('open');
  const openAlerts = useAlerts('open', open);
  const doneAlerts = useAlerts('done', open && tab === 'done');
  const bulletins = useBulletins(open);
  const api = useApi();
  const qc = useQueryClient();
  // what was unread when the centre opened stays highlighted while it is open
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const markedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      markedRef.current = false;
      return;
    }
    if (markedRef.current || !openAlerts.data) return;
    const unread = openAlerts.data.filter((a) => !a.read).map((a) => a.id);
    setFresh(new Set(unread));
    markedRef.current = true;
    if (unread.length) {
      void api('/alerts/actions/read', { method: 'POST', body: JSON.stringify({ ids: unread }) })
        .then(() => qc.invalidateQueries({ queryKey: keys.alertSummary }))
        .catch(() => undefined);
    }
  }, [open, openAlerts.data]);

  const news = bulletins.data ?? [];
  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={480}
      title={t('notifications.title')}
      styles={{ body: { paddingTop: 8 } }}
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as typeof tab)}
        items={[
          {
            key: 'open',
            label: (
              <Space size={6}>
                {t('notifications.tabs.open')}
                <Tag variant="filled" style={{ marginInlineEnd: 0 }}>
                  {summary.data?.open ?? 0}
                </Tag>
              </Space>
            ),
            children: (
              <AlertList
                key={family ?? 'all'}
                loading={openAlerts.isLoading}
                alerts={openAlerts.data ?? []}
                fresh={fresh}
                onNavigate={onClose}
                empty={t('notifications.emptyOpen')}
                grouped
                initialFamily={family}
              />
            ),
          },
          {
            key: 'done',
            label: t('notifications.tabs.done'),
            children: (
              <AlertList
                loading={doneAlerts.isLoading}
                alerts={doneAlerts.data ?? []}
                fresh={new Set()}
                onNavigate={onClose}
                empty={t('notifications.emptyDone')}
              />
            ),
          },
          {
            key: 'news',
            label: (
              <Badge dot={(summary.data?.unreadBulletins ?? 0) > 0} offset={[6, 0]}>
                {t('notifications.tabs.news')}
              </Badge>
            ),
            children: <BulletinList loading={bulletins.isLoading} bulletins={news} />,
          },
        ]}
      />
    </Drawer>
  );
}

function AlertList({
  alerts,
  loading,
  fresh,
  onNavigate,
  empty,
  grouped = false,
  initialFamily,
}: {
  alerts: AlertItem[];
  loading: boolean;
  fresh: Set<string>;
  onNavigate: () => void;
  empty: string;
  grouped?: boolean;
  initialFamily?: AlertFamily;
}) {
  const { t, i18n } = useTranslation();
  const [family, setFamily] = useState<AlertFamily | 'ALL'>(initialFamily ?? 'ALL');
  const families = useMemo(
    () => [...new Set(alerts.map((a) => a.family))] as AlertFamily[],
    [alerts],
  );
  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!alerts.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={empty} />;
  }
  const lang = locale(i18n.language);
  const shown = family === 'ALL' ? alerts : alerts.filter((a) => a.family === family);
  return (
    <Flex vertical gap={10}>
      {grouped && families.length > 1 && (
        <div className="wm-filter-scroll">
          <Segmented<AlertFamily | 'ALL'>
            size="small"
            value={family}
            onChange={setFamily}
            options={[
              { value: 'ALL', label: `${t('notifications.all')} ${alerts.length}` },
              ...families.map((f) => ({
                value: f,
                label: `${ALERT_FAMILY_LABELS[f][lang]} ${alerts.filter((a) => a.family === f).length}`,
              })),
            ]}
          />
        </div>
      )}
      {shown.map((a) => (
        <AlertCard key={a.id} a={a} fresh={fresh.has(a.id)} onNavigate={onNavigate} />
      ))}
    </Flex>
  );
}

function AlertCard({
  a,
  fresh,
  onNavigate,
}: {
  a: AlertItem;
  fresh: boolean;
  onNavigate: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const lang = locale(i18n.language);
  const text = describeAlert(a, lang);
  const [busy, setBusy] = useState(false);

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    try {
      await api(`/alerts/${a.id}/actions/${path}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.alerts }),
        qc.invalidateQueries({ queryKey: keys.alertSummary }),
      ]);
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const handled = a.acknowledgedAt || (a.snoozedUntil && dayjs(a.snoozedUntil).isAfter(dayjs()));
  const escalated =
    a.escalatedAt && !a.resolvedAt
      ? t('notifications.escalatedOn', { date: formatDate(a.escalatedAt) })
      : null;
  const status = a.resolvedAt
    ? t(`notifications.resolution.${a.resolution ?? 'CLEARED'}`, {
        date: formatDate(a.resolvedAt),
        defaultValue: t('notifications.resolution.CLEARED', { date: formatDate(a.resolvedAt) }),
      })
    : a.acknowledgedAt
      ? t('notifications.acknowledgedOn', { date: formatDate(a.acknowledgedAt) })
      : a.snoozedUntil && handled
        ? t('notifications.snoozedUntil', { date: formatDate(a.snoozedUntil) })
        : null;

  return (
    <div
      className={`wm-alert-card${fresh ? ' wm-alert-fresh' : ''}`}
      style={{ borderInlineStartColor: SEVERITY_COLOR[a.severity] }}
    >
      <Flex gap={10} align="flex-start">
        <span className="wm-alert-icon" style={{ color: SEVERITY_COLOR[a.severity] }}>
          {FAMILY_ICON[a.family]}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, lineHeight: 1.35 }}>{text.title}</div>
          {text.detail && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {text.detail}
            </Typography.Text>
          )}
          <Flex gap={6} wrap align="center" style={{ marginTop: 6 }}>
            {a.siteNames.map((s) => (
              <Tag key={s} style={{ marginInlineEnd: 0 }}>
                {s}
              </Tag>
            ))}
            {a.regulatory && (
              <Tooltip title={t('notifications.regulatoryHint')}>
                <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
                  {t('notifications.regulatory')}
                </Tag>
              </Tooltip>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(a.raisedAt).fromNow()}
            </Typography.Text>
          </Flex>
          {escalated && (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              {escalated}
            </Typography.Text>
          )}
          {status && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 4 }}
            >
              {status}
            </Typography.Text>
          )}
          <Flex gap={4} wrap style={{ marginTop: 8, marginInlineStart: -7 }}>
            <Button
              size="small"
              type="link"
              onClick={() => {
                onNavigate();
                navigate(alertPath(a));
              }}
            >
              {t('notifications.open')}
            </Button>
            {!a.resolvedAt && !handled && (
              <>
                <Button
                  size="small"
                  type="text"
                  icon={<CheckOutlined />}
                  loading={busy}
                  onClick={() => void act('acknowledge')}
                >
                  {t('notifications.acknowledge')}
                </Button>
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [1, 7, 30].map((days) => ({
                      key: String(days),
                      label: t('notifications.snoozeFor', { count: days }),
                    })),
                    onClick: ({ key }) => void act('snooze', { days: Number(key) }),
                  }}
                >
                  <Button size="small" type="text" icon={<ClockCircleOutlined />}>
                    {t('notifications.snooze')}
                  </Button>
                </Dropdown>
              </>
            )}
            {!a.resolvedAt && handled && (
              <Button
                size="small"
                type="text"
                icon={<UndoOutlined />}
                loading={busy}
                onClick={() => void act('reopen')}
              >
                {t('notifications.reopen')}
              </Button>
            )}
          </Flex>
        </div>
      </Flex>
    </div>
  );
}

function BulletinList({ bulletins, loading }: { bulletins: Bulletin[]; loading: boolean }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const lang = locale(i18n.language);
  if (loading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (!bulletins.length) {
    return (
      <Empty
        image={<NotificationOutlined style={{ fontSize: 40, color: brandRamp[130] }} />}
        description={t('notifications.emptyNews')}
      />
    );
  }
  const acknowledge = (b: Bulletin) =>
    api(`/bulletins/${b.id}/actions/acknowledge`, { method: 'POST' })
      .then(() => qc.invalidateQueries({ queryKey: keys.bulletins }))
      .catch(() => undefined);
  const read = (b: Bulletin) => {
    if (b.read) return;
    void api(`/bulletins/${b.id}/actions/read`, { method: 'POST' })
      .then(() =>
        Promise.all([
          qc.invalidateQueries({ queryKey: keys.bulletins }),
          qc.invalidateQueries({ queryKey: keys.alertSummary }),
        ]),
      )
      .catch(() => undefined);
  };
  return (
    <Collapse
      accordion
      onChange={(key) => {
        const id = Array.isArray(key) ? key[0] : key;
        const b = bulletins.find((x) => x.id === id);
        if (b) read(b);
      }}
      items={bulletins.map((b) => ({
        key: b.id,
        label: (
          <div>
            <Flex gap={6} align="center">
              {!b.read && <Badge status="processing" />}
              <span style={{ fontWeight: b.read ? 500 : 650 }}>{b.title[lang] ?? b.title.fr}</span>
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('notifications.publishedOn', { date: formatDate(b.publishedAt) })}
              {b.effectiveOn &&
                ` · ${t('notifications.effectiveOn', { date: formatDate(b.effectiveOn) })}`}
            </Typography.Text>
          </div>
        ),
        children: (
          <Flex vertical gap={8}>
            <Typography.Paragraph style={{ margin: 0 }}>
              {b.summary[lang] ?? b.summary.fr}
            </Typography.Paragraph>
            {b.body && (
              <Typography.Paragraph type="secondary" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {b.body}
              </Typography.Paragraph>
            )}
            {b.impacts.length > 0 && (
              <Flex gap={6} wrap align="center">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('notifications.impacts')}
                </Typography.Text>
                {b.impacts.map((i) => (
                  <Tag key={i}>{i}</Tag>
                ))}
              </Flex>
            )}
            {b.links.map((l) => (
              <a key={l.url} href={l.url} target="_blank" rel="noreferrer">
                <ReadOutlined /> {l.label}
              </a>
            ))}
            {b.refVersionId && (
              <Link to="/ref/impacts">
                <DiffOutlined /> {t('notifications.seeImpacts')}
              </Link>
            )}
            {b.acknowledgedAt ? (
              <Typography.Text type="success" style={{ fontSize: 12 }}>
                {t('notifications.acknowledgedBy', {
                  name: b.acknowledgedBy,
                  date: formatDate(b.acknowledgedAt.slice(0, 10)),
                })}
              </Typography.Text>
            ) : b.canAcknowledge ? (
              <div>
                <Button size="small" onClick={() => void acknowledge(b)}>
                  {t('notifications.acknowledgeBulletin')}
                </Button>
              </div>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('notifications.notAcknowledged')}
              </Typography.Text>
            )}
          </Flex>
        ),
      }))}
    />
  );
}
